/**
 * Depop's reference data, read through the seller's browser: the category tree every Depop listing
 * is filed under, and the sizes a kind of item takes. Our server cannot read either — Depop refuses
 * every request from our server's address — so both come from the seller's browser, through the
 * extension, and the tree is also kept on our server for every seller once a browser has fetched it.
 *
 * When either cannot be had, the seller is told so and the Depop save waits: a category or size we
 * could not check against Depop's own lists is one Depop may refuse.
 */

import { askBrowserTwice } from 'src/utils/ask-browser-twice';
import { voolistExtension } from 'src/utils/voolist-extension';

// The words every screen uses when Depop's categories, or an item's sizes, could not be loaded,
// and what the seller can do about it. The extension opens Depop by itself for these lookups; an
// open Depop tab gives it one ready to use.
export const DEPOP_CATEGORIES_NOT_LOADED = "Depop's categories didn't load.";
export const DEPOP_SIZES_NOT_LOADED = "Depop's sizes didn't load for this item.";
export const DEPOP_RETRY_ADVICE =
  'Click Try again. If it keeps happening, open the Depop website in another tab and try again.';

// ----------------------------------------------------------------------
// The category tree: departments (Men, Women, Kids, Everything else), the groups in each, and the
// product types in each group — Depop's answer to GET webapi.depop.com/api/v3/search/categoryFilters/.

/** Whether an answer is a tree a seller can pick from. */
export function hasDepartments (tree) {
  return Array.isArray(tree?.categoryFilters) && tree.categoryFilters.length > 0;
}

/**
 * Find Depop's category tree, in this order:
 *   1. the tree the server keeps;
 *   2. none kept — ask the seller's browser, through the extension, and ask once more after a short
 *      pause when the first try brings nothing;
 *   3. the browser answered — send the tree to the server, so the next seller gets it from there.
 *
 * @param {Object} sources
 * @param {function(): Promise<*>} sources.readKept - the tree the server keeps (anything else, or a
 *   rejection, means none)
 * @param {function(): Promise<*>} sources.askBrowser - the tree fetched through the extension
 * @param {function(Object): Promise<*>} sources.keep - send a fetched tree to the server
 * @param {function(number): Promise<void>} [sources.pause] - see askBrowserTwice
 * @returns {Promise<Object|null>} Depop's tree as Depop sent it, or null when it could not be found
 */
export async function findDepopCategoryTree ({ readKept, askBrowser, keep, pause }) {
  const kept = await readKept().catch(() => null);
  if (hasDepartments(kept)) return kept;

  const fetched = await askBrowserTwice(askBrowser, hasDepartments, pause);
  if (!fetched) return null;

  // Keeping the tree only spares the next seller a trip to their own browser. This seller already
  // has it, so failing to keep it changes nothing here.
  keep(fetched).catch(() => {});
  return fetched;
}

/**
 * The tree as the pickers use it: each department with its groups, and each group's product types.
 * Depop files product types under the group alone, so a group that several departments share
 * ('accessories' is in Men, Women and Kids) offers the same product types in each.
 *
 * The tree can come straight from the seller's browser, unchecked, so an entry without an id is left
 * out and a part that is not a list offers nothing, rather than stopping the whole picker loading.
 *
 * @param {Object} tree - Depop's tree, as findDepopCategoryTree returns it
 * @returns {{departments: Array<{id: string, label: string, groups: Array<{id: string, label: string}>}>,
 *   productTypes: Object<string, Array<{id: string, label: string}>>}}
 */
export function depopCategoryOptions (tree) {
  const entriesOf = (value) => (Array.isArray(value) ? value : [])
    .filter((entry) => typeof entry?.id === 'string' && entry.id)
    .map((entry) => ({ id: entry.id, label: entry.name, children: entry.children }));

  const departments = entriesOf(tree.categoryFilters).map(({ id, label, children }) => ({
    id,
    label,
    groups: entriesOf(children).map((group) => ({ id: group.id, label: group.label })),
  }));

  const productTypes = Object.fromEntries(
    Object.entries(tree.subcategoryFilterDictionary || {}).map(([group, list]) => [
      group,
      entriesOf(list).map((productType) => ({ id: productType.id, label: productType.label })),
    ])
  );

  return { departments, productTypes };
}

// ----------------------------------------------------------------------
// Sizes

/**
 * Whether the browser answered the size question: with sizes, or with "this kind of item has no
 * size" (`required: false`). Anything else means the sizes could not be loaded — which must never be
 * read as "no size needed", because Depop refuses a listing without a size it requires.
 *
 * @param {*} answer - what the extension sent back
 * @returns {boolean}
 */
export const isDepopSizeAnswer = (answer) => answer?.success === true;

/**
 * The sizes Depop offers for one department, group and product type, asked of the seller's browser —
 * a second time if the first try fails.
 *
 * @param {string} department
 * @param {string} group
 * @param {string} productType
 * @returns {Promise<{success: true, required: boolean, regions?: Object}|null>} Depop's answer, or
 *   null when the sizes could not be loaded
 */
export function lookUpDepopSizes (department, group, productType) {
  return askBrowserTwice(
    () => voolistExtension.getDepopSizes(department, group, productType),
    isDepopSizeAnswer,
  );
}
